/**
 * Ported VERBATIM from `apr_template/src/pages/ApplicationPortfolioScoring.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useDispositionPalette } from '../dispositionPalette';
import { useAPR } from '../compat/APRContext';
import { useAuth } from '../compat/AuthContext';
import apiService, { authFetch } from '../compat/api';
import ScoringDetailsModal from '../components/ScoringDetailsModal';
import QAAssessmentModal, { type AssessmentResults } from '../components/QAAssessmentModal';
import QuestionsAnswersModal from '../components/QuestionsAnswersModal';
import QAViewModal from '../components/QAViewModal';
import '../styles/ApplicationPortfolioScoring.css';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import { type Help } from '../compat/helpTypes';
import ApplicationNoteModal, { ApplicationNoteButton } from '../components/ApplicationNoteModal';
import {
  AssessmentStatusChip,
  DispositionChip,
  ActionButton,
  ActionState,
} from '../components/RowControls';

const ApplicationPortfolioScoringHelp: Help = {
  title: 'Initial Portfolio Scoring',
  icon: '📊',
  purpose:
    'Review the computed <strong>overall scores</strong> and <strong>recommendations</strong> from completed Q&amp;A assessments. Drill into each application\'s dimension/component breakdown and correct answers where needed.',
  doHere: [
    'Click <strong>View Details</strong> to open the full scoring breakdown by dimension and component.',
    'Use <strong>Q&amp;A Edit</strong> / <strong>Q&amp;A View</strong> to change or inspect answers (Admin/APR Analyst); editing triggers an automatic rescore.',
    'Use <strong>Continue</strong> to finish in-progress assessments, and (Administrator) <strong>Rescore</strong> to recompute using current component weights.',
    'Filter by <strong>Assessment Status</strong>, <strong>Owner</strong>, or <strong>Application ID</strong>; switch <strong>Tree</strong>/<strong>Flat</strong> views.',
    'Applications marked <strong>Transferred to Integration</strong> or with a <strong>Final</strong> disposition are locked from rescoring.',
  ],
  fits:
    'Application Evaluation &rarr; Initial Disposition &rarr; Initial Portfolio Scoring',
  firstTime:
    'Open a completed application\'s View Details to see how its recommendation was derived.',
  sections: [
    {
      type: 'defs',
      label: 'recommendation (score band)',
      items: [
        { term: 'Retire', color: '#7f1d1d', def: '1.00&ndash;2.00 &mdash; decommission the application.' },
        { term: 'Re-Platform', color: '#9a3412', def: '2.01&ndash;3.00 &mdash; move to a better platform.' },
        { term: 'Re-Design', color: '#1e3a8a', def: '3.01&ndash;4.00 &mdash; modernize / rebuild.' },
        { term: 'Retain', color: '#14532d', def: '4.01&ndash;5.00 &mdash; keep as-is.' },
      ],
      note: 'Overall score is the average of dimension scores on a 1.00&ndash;5.00 scale.',
    },
  ],
};

interface ScoringApplication {
  application_id: string;
  application_name: string;
  application_id_code: string;
  technology_stack: string;
  user_count: number;
  application_owner: string;
  app_owner_email: string;
  technical_owner_custodian: string;
  to_email: string;
  business_function_domain: string;
  overall_score: number;
  recommendation: string;
  total_questions: number;
  answered_questions: number;
  completion_percentage: number;
  assessment_status: 'Not Started' | 'In-Process' | 'Completed';
  dimension_scores: DimensionScore[];
  apr_run_id: string;
  apr_run_name: string;
  last_modified: string;
  transferred_to_integration: number;
  transferred_at?: string;
  transferred_by?: string;
  final_disposition?: string | null;  // When set, locks application from rescoring
}

interface ComponentScore {
  component_id: string;
  component_name: string;
  component_score: number;
  total_questions: number;
  answered_questions: number;
  completion_percentage: number;
}

interface DimensionScore {
  dimension_id: string;
  dimension_name: string;
  dimension_order: number;
  total_questions: number;
  answered_questions: number;
  dimension_score: number;
  component_scores?: ComponentScore[];
}

interface ScoringData {
  applications: ScoringApplication[];
  summary: {
    total_applications: number;
    completed_assessments: number;
    in_progress_assessments: number;
    avg_completion_percentage: string;
  };
}

// Type for grouped model data (same as QA page)
interface ModelGroup {
  modelId: string;
  modelName: string;
  modelType: 'Model' | 'Run';
  frameworkName?: string;
  applications: ScoringApplication[];
}

const ApplicationPortfolioScoring: React.FC = () => {
  const { state } = useAPR();
  const { currentUser } = useAuth();
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'completed' | 'in_progress'>('all');
  // Free-text owner filter (substring match), same as the Application ID filter.
  // Empty string = no filter.
  const [selectedOwner, setSelectedOwner] = useState<string>('');
  const [applicationIdFilter, setApplicationIdFilter] = useState<string>('');
  const [scoringData, setScoringData] = useState<ScoringData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDetailsWindow, setShowDetailsWindow] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<{ id: string; name: string; code?: string; aprRunId?: string } | null>(null);
  const [showQAModal, setShowQAModal] = useState(false);
  const [showQAEditModal, setShowQAEditModal] = useState(false);
  const [showQAViewModal, setShowQAViewModal] = useState(false);
  const [selectedQAApplication, setSelectedQAApplication] = useState<{ id: string; name: string; aprRunId?: string } | null>(null);
  const [rescoringApps, setRescoringApps] = useState<Set<string>>(new Set());
  /**
   * Applications carrying a note, so a row shows it without being opened.
   *
   * This screen is where the note is READ — an analyst looking at a 2.91 wants to know
   * whether the owner explained it before they act on the number.
   */
  const [notedApps, setNotedApps] = useState<Set<string>>(new Set());
  const [noteFor, setNoteFor] = useState<{ id: string; name: string; code?: string } | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [viewMode, setViewMode] = useState<'tree' | 'flat'>('tree');
  const [modelGroups, setModelGroups] = useState<ModelGroup[]>([]);
  // Initialize expandedModels from sessionStorage to preserve state across modal opens/data reloads
  const [expandedModels, setExpandedModels] = useState<Set<string>>(() => {
    try {
      const saved = sessionStorage.getItem('scoringExpandedModels');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  /** So the auto-expand runs once per mount and never re-opens what the user has closed. */
  const hasChosenExpansion = useRef(false);
  const [qaStatuses, setQAStatuses] = useState<Record<string, { totalQuestions: number; answeredQuestions: number; completionPercentage: number }>>({});

  // Persist expandedModels to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem('scoringExpandedModels', JSON.stringify(Array.from(expandedModels)));
  }, [expandedModels]);

  // Which applications carry a note. Loaded once with the screen; the indicator is
  // updated in place when a note is saved here, so no refetch is needed for that.
  const loadNotedApps = async () => {
    try {
      const response = await authFetch('/api/notes');
      if (!response.ok) return;
      const data = await response.json();
      setNotedApps(new Set(Object.keys(data.notes ?? {})));
    } catch {
      // The indicator is a convenience; never let it stop the scores loading.
    }
  };

  useEffect(() => {
    loadNotedApps();
  }, []);

  // Only selectedStatus reaches the server; the owner filter is applied client-side,
  // so it must not be a dependency here — otherwise every keystroke refetches.
  useEffect(() => {
    loadScoringData();
  }, [selectedStatus]);

  // Build Q&A statuses directly from scoring-portfolio data (already contains total_questions, answered_questions, completion_percentage)
  const buildQAStatuses = (applications: ScoringApplication[]) => {
    const statusMap: Record<string, { totalQuestions: number; answeredQuestions: number; completionPercentage: number }> = {};
    for (const app of applications) {
      statusMap[app.application_id] = {
        totalQuestions: app.total_questions || 0,
        answeredQuestions: app.answered_questions || 0,
        completionPercentage: app.completion_percentage || 0
      };
    }
    setQAStatuses(statusMap);
  };

  const loadScoringData = async () => {
    setLoading(true);
    try {
      const data = await apiService.getScoringPortfolioData(selectedStatus === 'all' ? undefined : selectedStatus);
      setScoringData(data);
      // Load QA statuses for all applications
      if (data?.applications && data.applications.length > 0) {
        buildQAStatuses(data.applications);
      }
    } catch (err) {
      // Treat load failures / no-data the same as an empty portfolio: the main
      // render shows a friendly "No Scoring Data Available" state — no error banner.
      console.error('Error loading scoring data:', err);
      setScoringData(null);
    }
    setLoading(false);
  };

  // Build model groups from APR context (same approach as QA page)
  const buildModelGroupsFromContext = async (applications: ScoringApplication[]): Promise<ModelGroup[]> => {
    const groups: ModelGroup[] = [];
    const appIdSet = new Set(applications.map(a => a.application_id));

    // Get In-Process APR runs
    const inProcessAPRRuns = state.aprRuns.filter(run => run.status === 'In-Process');

    // Get In-Process APR models
    const inProcessAPRModels = state.models.filter(model => model.status === 'In-Process');

    console.log('🏃 Scoring - In-Process APR Runs:', inProcessAPRRuns.map(r => ({ id: r.id, name: r.rationalizationName })));
    console.log('🎯 Scoring - In-Process APR Models:', inProcessAPRModels.map(m => ({ id: m.id, name: m.name })));

    // Build groups from APR Runs
    for (const run of inProcessAPRRuns) {
      if (run.selectedApplicationIds) {
        const runApps: ScoringApplication[] = [];
        for (const appId of run.selectedApplicationIds) {
          // Find the scoring application data for this app
          const scoringApp = applications.find(a => a.application_id === appId);
          if (scoringApp) {
            runApps.push(scoringApp);
          }
        }
        if (runApps.length > 0) {
          groups.push({
            modelId: run.id,
            modelName: run.rationalizationName,
            modelType: 'Run',
            applications: runApps
          });
        }
      }
    }

    // Build groups from APR Models
    for (const model of inProcessAPRModels) {
      try {
        const modelApplications = await apiService.getModelApplications(model.id);
        const modelApps: ScoringApplication[] = [];

        for (const app of modelApplications) {
          // Find the scoring application data for this app
          const scoringApp = applications.find(a => a.application_id === app.id);
          if (scoringApp) {
            modelApps.push(scoringApp);
          }
        }

        if (modelApps.length > 0) {
          groups.push({
            modelId: model.id,
            modelName: model.name,
            modelType: 'Model',
            frameworkName: (model as any).frameworkName || undefined,
            applications: modelApps
          });
        }
      } catch (error) {
        console.error(`Error loading applications for model ${model.id}:`, error);
      }
    }

    console.log('🌳 Scoring - Model Groups:', groups.map(g => ({ id: g.modelId, name: g.modelName, appCount: g.applications.length })));
    return groups;
  };

  // Update model groups when scoring data, APR context, or QA statuses change
  useEffect(() => {
    const loadGroups = async () => {
      if (scoringData?.applications && scoringData.applications.length > 0) {
        const groups = await buildModelGroupsFromContext(scoringData.applications);
        setModelGroups(groups);

        /*
         * OPEN THE GROUPS WHEN THERE ARE FEW, unless the user has already chosen otherwise
         * this session.
         *
         * The initial state came from sessionStorage and fell back to an EMPTY set, so a
         * first visit opened with every group shut. With one model holding one application
         * the screen renders a title and nothing beneath it — no rows, no scores, no
         * buttons — which reads as an empty or broken screen. It is also why a change to
         * those rows can look like no change at all: there was nothing on screen to change.
         *
         * `sessionStorage` still wins where it exists, so a deliberate collapse survives
         * navigation. Above five groups the collapsed overview is useful and stays.
         */
        if (!hasChosenExpansion.current && groups.length > 0 && groups.length <= 5) {
          hasChosenExpansion.current = true;
          setExpandedModels((prev) => (prev.size > 0 ? prev : new Set(groups.map((g) => g.modelId))));
        }
        // Note: expandedModels is preserved via sessionStorage, not reset here
      }
    };
    loadGroups();
  }, [scoringData, state.aprRuns, state.models, qaStatuses]);

  // Toggle model expansion in tree view
  const toggleModelExpansion = (modelId: string) => {
    setExpandedModels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(modelId)) {
        newSet.delete(modelId);
      } else {
        newSet.add(modelId);
      }
      return newSet;
    });
  };

  // Get aggregate stats for a model group (using qaStatuses like QA page)
  const getModelStats = (group: ModelGroup) => {
    let totalQuestions = 0;
    let answeredQuestions = 0;
    let completedApps = 0;
    let inProgressApps = 0;
    let notStartedApps = 0;

    group.applications.forEach(app => {
      // Use qaStatuses for accurate question counts (same as QA page)
      const status = qaStatuses[app.application_id];
      if (status) {
        totalQuestions += status.totalQuestions || 0;
        answeredQuestions += status.answeredQuestions || 0;
        // Determine app status based on answered questions
        if (status.answeredQuestions === 0) {
          notStartedApps++;
        } else if (status.answeredQuestions < status.totalQuestions) {
          inProgressApps++;
        } else {
          completedApps++;
        }
      } else {
        // Fallback to scoring data
        totalQuestions += app.total_questions || 0;
        answeredQuestions += app.answered_questions || 0;
        if (app.assessment_status === 'Completed') {
          completedApps++;
        } else if (app.assessment_status === 'In-Process') {
          inProgressApps++;
        } else {
          notStartedApps++;
        }
      }
    });

    return {
      totalQuestions,
      answeredQuestions,
      completedApps,
      inProgressApps,
      notStartedApps,
      totalApps: group.applications.length,
      completionPercentage: totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0
    };
  };

  // Filter applications within a model group
  const filterGroupApplications = (apps: ScoringApplication[]) => {
    return apps.filter(app => {
      const statusMatch = selectedStatus === 'all' ||
        (selectedStatus === 'completed' && app.assessment_status === 'Completed') ||
        (selectedStatus === 'in_progress' && app.assessment_status === 'In-Process');

      const ownerMatch = !selectedOwner.trim() ||
        (app.application_owner || '').toLowerCase().includes(selectedOwner.trim().toLowerCase());

      const applicationIdMatch = !applicationIdFilter.trim() ||
        (app.application_id_code && app.application_id_code.toLowerCase().includes(applicationIdFilter.toLowerCase())) ||
        app.application_name.toLowerCase().includes(applicationIdFilter.toLowerCase());

      return statusMatch && ownerMatch && applicationIdMatch;
    });
  };

  /**
   * The disposition's colour, FROM THE DISPOSITION MATRIX.
   *
   * This was a fourth hardcoded palette — and it disagreed with the matrix on three of the
   * four outcomes. Re-Design is amber in `apr.scoring_thresholds` and was drawn BLUE here, so
   * the same application showed a different colour depending which screen you opened. Editing
   * the colour picker on the Disposition Matrix screen changed neither.
   *
   * `ink` rather than the stored value because these colours are FILLS: Re-Design's #ffc107
   * reads at 1.63:1 as text on white. The ink is the same hue darkened until it clears 4.5:1.
   */
  const palette = useDispositionPalette();
  const getRecommendationColor = (recommendation: string) => palette(recommendation).ink;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return '#28a745';
      case 'In-Process': return '#ffc107';
      case 'Not Started': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getUniqueOwners = (applications: ScoringApplication[]) => {
    const owners = applications
      .map(app => app.application_owner)
      .filter(owner => owner && owner.trim() !== '')
      .filter((owner, index, arr) => arr.indexOf(owner) === index)
      .sort();
    return owners;
  };

  const formatScore = (score: number | string | null) => {
    if (score === null || score === undefined || score === '') return 'N/A';
    // Postgres numeric columns come through as strings via the pg driver.
    const v = typeof score === 'number' ? score : Number(score);
    return Number.isFinite(v) ? v.toFixed(2) : 'N/A';
  };

  const handleViewDetails = (applicationId: string, applicationName: string, applicationCode?: string) => {
    setSelectedApplication({ id: applicationId, name: applicationName, code: applicationCode });
    setShowDetailsWindow(true);
  };

  const handleCloseDetailsWindow = () => {
    setShowDetailsWindow(false);
    setSelectedApplication(null);
  };

  const handleViewEditQuestions = (applicationId: string, applicationName: string, aprRunId?: string) => {
    setSelectedQAApplication({ id: applicationId, name: applicationName, aprRunId });

    // Check if application is transferred to integration or has final disposition set
    const app = scoringData?.applications.find(a => a.application_id === applicationId);
    const isTransferred = app?.transferred_to_integration === 1;
    const hasDisposition = !!app?.final_disposition;
    const isLocked = isTransferred || hasDisposition;

    if (isLocked) {
      // Show read-only view modal for locked applications
      setShowQAViewModal(true);
    } else {
      // Show editable modal for non-locked applications
      setShowQAEditModal(true);
    }
  };

  const handleCloseQAEditModal = async () => {
    setShowQAEditModal(false);

    // Trigger rescoring for the application if it was edited
    if (selectedQAApplication) {
      try {
        console.log(`Triggering rescore for application: ${selectedQAApplication.name}`);
        await apiService.rescoreApplication(selectedQAApplication.id, selectedQAApplication.aprRunId);
        console.log('Rescore completed successfully');
      } catch (error) {
        console.error('Error rescoring application:', error);
        // Continue even if rescoring fails - the data might still be updated
      }
    }

    setSelectedQAApplication(null);

    // Reload scoring data to reflect any changes made
    await loadScoringData();
  };

  const handleCloseQAViewModal = () => {
    setShowQAViewModal(false);
    setSelectedQAApplication(null);
  };

  const handleContinueQA = (app: ScoringApplication, event?: React.MouseEvent) => {
    // Prevent event bubbling and double clicks
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Prevent opening if already opening or modal is already open
    if (showQAModal) return;

    // Check if Q&A assessment has been initiated (has any answered questions)
    if (app.answered_questions === 0) {
      setNotification({
        message: `No Q&A assessment has been initiated for "${app.application_name}". Please start the Q&A assessment from the APR Execution → Q&A Assessment screen first.`,
        type: 'error'
      });
      setTimeout(() => setNotification(null), 5000);
      return;
    }

    // Find the APR run ID for this application
    let aprRunId: string | undefined;

    // First check in APR runs
    const aprRun = state.aprRuns.find(run =>
      run.status === 'In-Process' &&
      run.selectedApplicationIds?.includes(app.application_id)
    );

    if (aprRun) {
      aprRunId = aprRun.id;
    }

    // Set selected application and show modal
    setSelectedApplication({
      id: app.application_id,
      name: app.application_name,
      aprRunId
    });

    setShowQAModal(true);
  };

  const handleCloseQAModal = async () => {
    setShowQAModal(false);
    setSelectedApplication(null);
    
    // Reload scoring data to get updated scores and status after saving
    await loadScoringData();
  };

  const handleCompleteAssessment = async (results: AssessmentResults) => {
    console.log('Q&A Assessment completed:', results);
    
    // Close the modal first
    setShowQAModal(false);
    setSelectedApplication(null);
    
    // Reload scoring data to get updated completion status
    await loadScoringData();
  };

  const handleApplicationRescore = async (applicationId: string, applicationName: string, aprRunId?: string) => {
    // Only administrators can rescore
    if (currentUser?.role !== 'Administrator') {
      setNotification({
        message: 'Only administrators can perform rescoring operations.',
        type: 'error'
      });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    // Open the QA Assessment Modal for rescoring
    console.log(`Opening rescore modal for application: ${applicationName}`);
    setSelectedQAApplication({
      id: applicationId,
      name: applicationName,
      aprRunId: aprRunId
    });
    setShowQAEditModal(true);
  };


  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Initial Application Portfolio Scoring" subtitle="Loading scoring data..." />
        <div className="master-data-container">
          <div className="empty-state">
            <div className="empty-state-icon">⏳</div>
            <h3>Loading Scoring Data</h3>
            <p>Please wait while we fetch application scoring data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page page-fixed-header scoring-page">
      <HelpPanel help={ApplicationPortfolioScoringHelp} />
      <PageHeader
        title="Initial Application Portfolio Scoring"
        subtitle="Review and analyze Q&A assessment scores and recommendations for applications"
      />

      {/* Main Content Container */}
      <div className="master-data-container">

      {/* Notification Display */}
      {notification && (
        <div className={`notification notification--${notification.type}`} 
             style={{
               position: 'fixed',
               top: '80px',
               right: '20px',
               zIndex: 1000,
               padding: '12px 20px',
               borderRadius: '4px',
               boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
               backgroundColor: notification.type === 'success' ? '#4caf50' : 
                              notification.type === 'error' ? '#f44336' : '#2196f3',
               color: 'white',
               display: 'flex',
               alignItems: 'center',
               gap: '10px',
               animation: 'slideIn 0.3s ease-out'
             }}>
          <span style={{ fontSize: '20px' }}>
            {notification.type === 'success' ? '✅' : 
             notification.type === 'error' ? '❌' : 'ℹ️'}
          </span>
          <span>{notification.message}</span>
        </div>
      )}

      <div className="md-content" style={{ maxWidth: 'none', padding: 0 }}>
        {/* Applications Display */}
        {!scoringData || scoringData.applications.length === 0 ? (
          <div className="md-empty-state">
            <div className="md-empty-state__icon">📊</div>
            <h3 className="md-empty-state__title">No data to present</h3>
            <p className="md-empty-state__description">
              {selectedStatus === 'all'
                ? 'There is no scoring data to present yet.'
                : `No applications match the selected filter "${selectedStatus}".`
              }
            </p>
          </div>
        ) : (
          <>
            {/* Filter Section */}
            <div className="md-filter-container" style={{
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              display: 'flex',
              gap: '20px',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              {/* Application ID Filter */}
              <div className="md-filter-section" style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                <label htmlFor="applicationIdFilter" className="md-filter-label" style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>Filter by Application ID</label>
                <input
                  type="text"
                  id="applicationIdFilter"
                  value={applicationIdFilter}
                  onChange={(e) => setApplicationIdFilter(e.target.value)}
                  placeholder="Enter Application ID..."
                  className="md-input"
                  /* Sizing comes from .md-input so this lines up with the selects.
                     Only the extra right padding is local — it clears the × button. */
                  style={{ paddingRight: '34px' }}
                />
                {applicationIdFilter && (
                  <button
                    onClick={() => setApplicationIdFilter('')}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      /* Anchor to the input's bottom edge, not a hard-coded top: the
                         input is the last child, so this stays centred in it. */
                      bottom: '12px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px',
                      color: '#666',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Clear Application ID filter"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Scoring Status Filter */}
              <div className="md-filter-section" style={{ flex: 1, minWidth: '200px' }}>
                <label htmlFor="statusFilter" className="md-filter-label" style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>Filter by Assessment Status</label>
                <select
                  id="statusFilter"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value as 'all' | 'completed' | 'in_progress')}
                  className="md-select"
                >
                  <option value="all">All Applications</option>
                  <option value="completed">Assessment Completed</option>
                  <option value="in_progress">Assessment In-Process</option>
                </select>
              </div>

              {/* Application Owner Filter */}
              <div className="md-filter-section" style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                <label htmlFor="ownerFilter" className="md-filter-label" style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>Filter by Application Owner</label>
                <input
                  type="text"
                  id="ownerFilter"
                  value={selectedOwner}
                  onChange={(e) => setSelectedOwner(e.target.value)}
                  placeholder="Enter Application Owner..."
                  className="md-input"
                  style={{ paddingRight: '34px' }}
                />
                {selectedOwner && (
                  <button
                    onClick={() => setSelectedOwner('')}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      bottom: '12px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px',
                      color: '#666',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Clear Application Owner filter"
                  >
                    ×
                  </button>
                )}
              </div>


              {/* View Mode Toggle */}
              <div className="md-filter-section" style={{ minWidth: '180px' }}>
                <label className="md-filter-label" style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>View Mode</label>
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  backgroundColor: '#e5e5e5',
                  borderRadius: '6px',
                  padding: '4px',
                  /* Match the 44px of .md-input / .md-select so the whole filter row
                     sits on one line — the row is align-items:center, so a shorter
                     control here also drags its label out of alignment. */
                  height: '44px',
                  boxSizing: 'border-box'
                }}>
                  <button
                    onClick={() => setViewMode('tree')}
                    style={{
                      flex: 1,
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      backgroundColor: viewMode === 'tree' ? '#fff' : 'transparent',
                      color: viewMode === 'tree' ? '#1a73e8' : '#666',
                      boxShadow: viewMode === 'tree' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      transition: 'all 0.2s'
                    }}
                  >
                    Tree
                  </button>
                  <button
                    onClick={() => setViewMode('flat')}
                    style={{
                      flex: 1,
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      backgroundColor: viewMode === 'flat' ? '#fff' : 'transparent',
                      color: viewMode === 'flat' ? '#1a73e8' : '#666',
                      boxShadow: viewMode === 'flat' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      transition: 'all 0.2s'
                    }}
                  >
                    Flat
                  </button>
                </div>
              </div>
            </div>

            {/* Only the data below scrolls — the header and filters above stay put. */}
            <div className="scrollable-data-section">

            {/* TREE VIEW */}
            {viewMode === 'tree' && (
              <div className="tree-view-container" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {modelGroups.map(group => {
                  const filteredApps = filterGroupApplications(group.applications);
                  if (filteredApps.length === 0) return null;
                  const isExpanded = expandedModels.has(group.modelId);
                  const stats = getModelStats({ ...group, applications: filteredApps });

                  return (
                    <div key={group.modelId} className="model-tree-node" style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      {/* Model Header (Parent Node) */}
                      <div
                        onClick={() => toggleModelExpansion(group.modelId)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '14px 16px',
                          backgroundColor: '#f8f9fa',
                          cursor: 'pointer',
                          borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none',
                          transition: 'background-color 0.2s'
                        }}
                      >
                        {/* Expand/Collapse Icon */}
                        <span style={{
                          marginRight: '12px',
                          fontSize: '14px',
                          color: '#666',
                          transition: 'transform 0.2s',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                        }}>
                          ▶
                        </span>

                        {/* Model Icon & Name */}
                        <span style={{ marginRight: '10px', fontSize: '1.3rem' }}>
                          {group.modelType === 'Model' ? '📁' : '🏃'}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', fontSize: '15px', color: '#333' }}>
                            {group.modelName}
                          </div>
                          {group.frameworkName && (
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                              Framework: {group.frameworkName}
                            </div>
                          )}
                        </div>

                        {/* Stats Badges */}
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <span className="md-count-chip md-count-chip--total">
                            {stats.totalApps} apps
                          </span>
                          {stats.completedApps > 0 && (
                            <span className="md-count-chip md-count-chip--done">
                              {stats.completedApps} complete
                            </span>
                          )}
                          {stats.inProgressApps > 0 && (
                            <span className="md-count-chip md-count-chip--progress">
                              {stats.inProgressApps} in progress
                            </span>
                          )}
                          <div style={{
                            width: '80px',
                            height: '6px',
                            backgroundColor: '#e0e0e0',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${stats.completionPercentage}%`,
                              height: '100%',
                              backgroundColor: stats.completionPercentage === 100 ? '#4caf50' : '#1a73e8',
                              transition: 'width 0.3s'
                            }} />
                          </div>
                          <span style={{ fontSize: '12px', color: '#666', minWidth: '35px' }}>
                            {stats.completionPercentage}%
                          </span>
                        </div>
                      </div>

                      {/* Applications Table (Children) */}
                      {isExpanded && (
                        <div className="md-table-container" style={{ borderRadius: 0 }}>
                          <table className="md-table" style={{ marginBottom: 0 }}>
                            <thead className="md-table__header">
                              <tr>
                                {/* The Q&A column is gone: it held ONE button that does the same
                                    kind of thing as the ones in Actions, so a row offered four
                                    controls across two columns and neither column could fit them
                                    on a line. One place for things you can do. */}
                                {/* Actions takes 38%: four labelled controls need about 420px and
                                    anything less clipped the first one against the cell edge. The
                                    width comes from Application and Owner, which both wrap happily
                                    and were carrying more than their content needed. */}
                                <th className="md-table__cell md-table__cell--header" style={{ width: '19%', textAlign: 'left' }}>Application</th>
                                <th className="md-table__cell md-table__cell--header" style={{ width: '10%', textAlign: 'center' }}>Status</th>
                                <th className="md-table__cell md-table__cell--header md-table__cell--score" style={{ width: '9%' }}>Score</th>
                                <th className="md-table__cell md-table__cell--header" style={{ width: '13%', textAlign: 'center' }}>Disposition</th>
                                <th className="md-table__cell md-table__cell--header" style={{ width: '13%', textAlign: 'left' }}>Owner</th>
                                <th className="md-table__cell md-table__cell--header" style={{ width: '38%', textAlign: 'right' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody className="md-table__body">
                              {filteredApps.map((app) => {
                                const isTransferred = app.transferred_to_integration === 1;
                                const hasDisposition = !!app.final_disposition;
                                const isLocked = isTransferred || hasDisposition;
                                return (
                                  <tr
                                    key={app.application_id}
                                    className={`md-table__row ${isLocked ? 'md-table__row--transferred' : ''}`}
                                    style={hasDisposition && !isTransferred ? {
                                      opacity: 0.6,
                                      backgroundColor: '#f5f5f5'
                                    } : undefined}
                                    title={hasDisposition ? `Final Disposition set: ${app.final_disposition} - Rescoring disabled` : undefined}
                                  >
                                    <td className="md-table__cell">
                                      <div className="md-app-info">
                                        <div className="md-app-info__primary" style={{ fontSize: '14px' }}>{app.application_id_code}</div>
                                        <div className="md-app-info__primary" style={{ fontSize: '14px' }}>{app.application_name}</div>
                                      </div>
                                    </td>
                                    <td className="md-table__cell" style={{ textAlign: 'center' }}>
                                      <AssessmentStatusChip status={app.assessment_status} />
                                    </td>
                                    {/* The score is CENTRED and uncoloured: the disposition chip
                                        beside it already carries the colour at full strength, and
                                        colouring the number too says the same thing twice. */}
                                    <td className="md-table__cell md-table__cell--score">
                                      {app.overall_score ? (
                                        <div className="md-score-value">{formatScore(app.overall_score)}</div>
                                      ) : (
                                        <span style={{ fontSize: '14px', color: '#999' }}>-</span>
                                      )}
                                    </td>
                                    <td className="md-table__cell" style={{ textAlign: 'center' }}>
                                      {app.recommendation ? (
                                        <DispositionChip value={app.recommendation} />
                                      ) : (
                                        <span style={{ fontSize: '12px', color: '#999' }}>-</span>
                                      )}
                                    </td>
                                    <td className="md-table__cell">
                                      <div style={{ fontSize: '14px' }}>{app.application_owner || '-'}</div>
                                    </td>
                                    <td className="md-table__cell">
                                      <div className="md-action-row">
                                        {app.assessment_status === 'Completed' && (
                                          <ActionButton
                                            primary
                                            icon="details"
                                            label="View Details"
                                            title="View detailed scoring breakdown"
                                            onClick={() => handleViewDetails(app.application_id, app.application_name, app.application_id_code)}
                                          />
                                        )}
                                        {(currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst') && app.assessment_status === 'Completed' && (
                                          <ActionButton
                                            icon={isLocked ? 'view' : 'edit'}
                                            label={isLocked ? 'Q&A View' : 'Q&A Edit'}
                                            title={isLocked ? 'View Q&A (Locked)' : 'Edit Q&A'}
                                            onClick={() => handleViewEditQuestions(app.application_id, app.application_name, app.apr_run_id)}
                                          />
                                        )}
                                        <ApplicationNoteButton
                                          hasNote={notedApps.has(app.application_id)}
                                          onClick={() =>
                                            setNoteFor({
                                              id: app.application_id,
                                              name: app.application_name,
                                              code: app.application_id_code,
                                            })
                                          }
                                        />
                                        {!isLocked && app.assessment_status === 'In-Process' && (
                                          <button
                                            className="qa-action-btn qa-action-btn--continue qa-action-btn--compact"
                                            onClick={(e) => handleContinueQA(app, e)}
                                          >
                                            <span className="qa-action-btn__text">Continue</span>
                                          </button>
                                        )}
                                        {!isLocked && currentUser?.role === 'Administrator' && app.assessment_status === 'Completed' && (
                                          <ActionButton
                                            icon="rescore"
                                            label={rescoringApps.has(app.application_id) ? 'Rescoring...' : 'Rescore'}
                                            title="Recalculate scores using current component weights"
                                            disabled={rescoringApps.has(app.application_id)}
                                            onClick={() => handleApplicationRescore(app.application_id, app.application_name, app.apr_run_id)}
                                          />
                                        )}
                                        {isTransferred && (
                                          <ActionState tone="green" icon="done" label="Transferred to Integration" />
                                        )}
                                        {hasDisposition && !isTransferred && (
                                          <ActionState tone="grey" icon="locked" label={`Final: ${app.final_disposition}`} />
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* FLAT VIEW */}
            {viewMode === 'flat' && (
            <div className="md-card md-card--elevated">
            <div className="md-table-container">
              <table className="md-table">
                <thead className="md-table__header">
                  <tr>
                    <th className="md-table__cell md-table__cell--header md-table__cell--numeric">#</th>
                    <th className="md-table__cell md-table__cell--header">Application</th>
                    <th className="md-table__cell md-table__cell--header">Assessment Status</th>
                    <th className="md-table__cell md-table__cell--header">Overall Score</th>
                    <th className="md-table__cell md-table__cell--header">Disposition</th>
                    <th className="md-table__cell md-table__cell--header">Application Owner</th>
                    <th className="md-table__cell md-table__cell--header" style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="md-table__body">

              {(() => {
                // Filter applications by status, owner, and application ID
                const filteredApplications = scoringData.applications.filter(app => {
                  const statusMatch = selectedStatus === 'all' ||
                    (selectedStatus === 'completed' && app.assessment_status === 'Completed') ||
                    (selectedStatus === 'in_progress' && app.assessment_status === 'In-Process');

                  const ownerMatch = !selectedOwner.trim() ||
                    (app.application_owner || '').toLowerCase().includes(selectedOwner.trim().toLowerCase());

                  const applicationIdMatch = !applicationIdFilter.trim() ||
                    (app.application_id_code && app.application_id_code.toLowerCase().includes(applicationIdFilter.toLowerCase())) ||
                    app.application_name.toLowerCase().includes(applicationIdFilter.toLowerCase());

                  return statusMatch && ownerMatch && applicationIdMatch;
                });

                // Sort applications by assessment status priority: In-Process → Completed → Not Started
                const sortedApplications = filteredApplications.sort((a, b) => {
                  const statusPriority: Record<string, number> = {
                    'In-Process': 1,
                    'Completed': 2,
                    'Not Started': 3
                  };

                  const aPriority = statusPriority[a.assessment_status] || 4;
                  const bPriority = statusPriority[b.assessment_status] || 4;

                  if (aPriority !== bPriority) {
                    return aPriority - bPriority;
                  }

                  // If same status, sort by application name
                  return a.application_name.localeCompare(b.application_name);
                });

                return sortedApplications;
              })().map((app, index) => {
                const isTransferred = app.transferred_to_integration === 1;
                const hasDisposition = !!app.final_disposition;
                const isLocked = isTransferred || hasDisposition;
                return (
                <tr
                  key={app.application_id}
                  className={`md-table__row ${isLocked ? 'md-table__row--transferred' : ''}`}
                  style={hasDisposition && !isTransferred ? {
                    opacity: 0.6,
                    backgroundColor: '#f5f5f5'
                  } : undefined}
                  title={hasDisposition ? `Final Disposition set: ${app.final_disposition} - Rescoring disabled` : undefined}
                >
                  <td className="md-table__cell md-table__cell--numeric">
                    <span className="md-row-number">{index + 1}</span>
                  </td>
                  
                  <td className="md-table__cell">
                    <div className="md-app-info">
                      <div className="md-app-info__primary">
                        {app.application_id_code}
                      </div>
                      <div className="md-app-info__primary">
                        {app.application_name}
                      </div>
                      <div className="md-app-info__caption">{app.technology_stack || 'Not specified'}</div>
                      {isTransferred && app.transferred_at && (
                        <div className="md-app-info__transfer-info" style={{fontSize: '11px', color: '#666', marginTop: '4px'}}>
                          Transferred: {new Date(app.transferred_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td className="md-table__cell">
                    <div className="md-assessment-status">
                      <AssessmentStatusChip status={app.assessment_status} />
                      <div className="md-progress-info">
                        {/* Just the count. The word "questions" repeats the column, and
                            the percentage repeats the bar directly beneath it. */}
                        <div className="md-progress-text">
                          {app.answered_questions}/{app.total_questions}
                        </div>
                        <div className="md-progress-bar">
                          <div 
                            className="md-progress-bar__fill"
                            style={{ 
                              width: `${app.completion_percentage}%`
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="md-table__cell">
                    <div className="md-score-info">
                      {app.overall_score ? (
                        <>
                          <div className="md-score-value">{formatScore(app.overall_score)}/5.0</div>
                          <div className="md-score-bar">
                            <div 
                              className="md-score-bar__fill"
                              style={{ 
                                width: `${(app.overall_score / 5) * 100}%`,
                                /* The BAR is a fill, so it takes the matrix's stored colour
                                   unchanged — the darkened ink is for text and rules only. */
                                backgroundColor: palette(app.recommendation).base
                              }}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="md-score-pending">
                          {app.completion_percentage < 100 ? (
                            <span title="Score will be calculated when all questions are answered">
                              Incomplete ({Math.round(app.completion_percentage)}%)
                            </span>
                          ) : (
                            'Pending'
                          )}
                        </div>
                      )}
                    </div>
                  </td>


                  <td className="md-table__cell">
                    <div className="md-recommendation">
                      {app.recommendation ? (
                        <DispositionChip value={app.recommendation} />
                      ) : (
                        <div className="md-recommendation-pending" style={{ fontSize: '16px' }}>
                          {app.completion_percentage < 100 ? (
                            <span title="Recommendation will be generated when all questions are answered">
                              Complete assessment first
                            </span>
                          ) : (
                            'To be determined'
                          )}
                        </div>
                      )}
                    </div>
                  </td>


                  <td className="md-table__cell">
                    <div className="md-owner-info">
                      {app.application_owner ? (
                        <>
                          <div className="md-owner-info__name">{app.application_owner}</div>
                          {app.app_owner_email && (
                            <div className="md-owner-info__email">{app.app_owner_email}</div>
                          )}
                        </>
                      ) : (
                        <span className="md-owners-none">Not assigned</span>
                      )}
                    </div>
                  </td>

                  <td className="md-table__cell">
                    <div className="md-action-row">
                      {app.assessment_status === 'Completed' && (
                        <ActionButton
                          primary
                          icon="details"
                          label="View Details"
                          title="View detailed scoring breakdown"
                          onClick={() => handleViewDetails(app.application_id, app.application_name, app.application_id_code)}
                        />
                      )}
                      {(currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst') && app.assessment_status === 'Completed' && (
                        <ActionButton
                          icon={isLocked ? 'view' : 'edit'}
                          label={isLocked ? 'Q&A View' : 'Q&A Edit'}
                          title={isLocked ? 'View questions & answers (read-only)' : 'View and edit questions & answers'}
                          onClick={() => handleViewEditQuestions(app.application_id, app.application_name, app.apr_run_id)}
                        />
                      )}
                      <ApplicationNoteButton
                        hasNote={notedApps.has(app.application_id)}
                        onClick={() =>
                          setNoteFor({
                            id: app.application_id,
                            name: app.application_name,
                            code: app.application_id_code,
                          })
                        }
                      />
                      {!isLocked && app.assessment_status === 'Not Started' && (
                        <button
                          className="qa-action-btn qa-action-btn--start qa-action-btn--compact"
                          onClick={(e) => handleContinueQA(app, e)}
                          title="Start Q&A assessment"
                        >
                          <span className="qa-action-btn__text">Start Q&A</span>
                        </button>
                      )}
                      {!isLocked && app.assessment_status === 'In-Process' && (
                        <button
                          className="qa-action-btn qa-action-btn--continue qa-action-btn--compact"
                          onClick={(e) => handleContinueQA(app, e)}
                          title="Continue Q&A assessment"
                        >
                          <span className="qa-action-btn__text">Continue</span>
                        </button>
                      )}
                      {!isLocked && currentUser?.role === 'Administrator' && app.assessment_status === 'Completed' && (
                        <ActionButton
                          icon="rescore"
                          label={rescoringApps.has(app.application_id) ? 'Rescoring...' : 'Rescore'}
                          title="Recalculate scores using current component weights"
                          disabled={rescoringApps.has(app.application_id)}
                          onClick={() => handleApplicationRescore(app.application_id, app.application_name, app.apr_run_id)}
                        />
                      )}
                      {isTransferred && (
                        <ActionState tone="green" icon="done" label="Transferred to Integration" />
                      )}
                      {hasDisposition && !isTransferred && (
                        <ActionState tone="grey" icon="locked" label={`Final: ${app.final_disposition}`} />
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
                </tbody>
              </table>
            </div>
          </div>
            )}

            </div>{/* /.scrollable-data-section */}
          </>
        )}
      </div>

      {noteFor && (
        <ApplicationNoteModal
          applicationId={noteFor.id}
          applicationName={noteFor.name}
          applicationCode={noteFor.code}
          onClose={() => setNoteFor(null)}
          onSaved={(id, note) =>
            setNotedApps((prev) => {
              const next = new Set(prev);
              if (note.trim()) next.add(id);
              else next.delete(id);
              return next;
            })
          }
        />
      )}

      {/* Scoring Details Modal */}
      {selectedApplication && showDetailsWindow && (
        <ScoringDetailsModal
          applicationId={selectedApplication.id}
          applicationName={selectedApplication.name}
          applicationCode={selectedApplication.code}
          isOpen={showDetailsWindow}
          onClose={handleCloseDetailsWindow}
        />
      )}

      {/* Q&A Assessment Modal */}
      {showQAModal && selectedApplication && (
        <QAAssessmentModal
          applicationId={selectedApplication.id}
          applicationName={selectedApplication.name}
          aprRunId={selectedApplication.aprRunId}
          isOpen={showQAModal}
          onClose={handleCloseQAModal}
          onComplete={handleCompleteAssessment}
        />
      )}

      {/* Questions & Answers Edit Modal (Rescore) */}
      {showQAEditModal && selectedQAApplication && (
        <QuestionsAnswersModal
          applicationId={selectedQAApplication.id}
          applicationName={selectedQAApplication.name}
          aprRunId={selectedQAApplication.aprRunId}
          isOpen={showQAEditModal}
          onClose={handleCloseQAEditModal}
          onSave={loadScoringData}
        />
      )}

      {/* Questions & Answers View Modal (Read-only) */}
      {showQAViewModal && selectedQAApplication && (
        <QAViewModal
          applicationId={selectedQAApplication.id}
          applicationName={selectedQAApplication.name}
          aprRunId={selectedQAApplication.aprRunId}
          isOpen={showQAViewModal}
          onClose={handleCloseQAViewModal}
        />
      )}
      </div>
    </div>
  );
};

export default ApplicationPortfolioScoring;